/**
 * `/agents/:id/history` — runs filtered to a single agent (RLY-024 AC #6).
 */
import { Link, useParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAgentDetail } from "@/hooks/use-agents";
import { useRunsList } from "@/hooks/use-runs";
import {
  formatCostMicros,
  formatDurationMs,
  formatTimestamp,
  runDurationMs,
  runStatusToBadgeVariant,
} from "@/lib/formatters";

export function AgentHistoryPage() {
  const { id } = useParams();
  const agent = useAgentDetail(id);
  const runs = useRunsList(id ? { agentId: id, limit: 50 } : { limit: 0 });

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Run history</h1>
          <p className="text-sm text-muted-foreground">
            All runs for{" "}
            <span data-testid="agent-history-agent-name" className="font-medium text-foreground">
              {agent.data?.name ?? "this agent"}
            </span>
            .
          </p>
        </div>
        <Link to={id ? `/agents/${id}` : "/agents"}>
          <Button variant="outline" size="sm" data-testid="agent-history-back">
            Back to agent
          </Button>
        </Link>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Runs</CardTitle>
          <CardDescription>Newest first.</CardDescription>
        </CardHeader>
        <CardContent>
          {runs.isLoading && (
            <div className="flex flex-col gap-2" data-testid="agent-history-loading">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          )}
          {runs.error && (
            <p data-testid="agent-history-error" className="text-sm text-destructive">
              {runs.error.message}
            </p>
          )}
          {runs.data && runs.data.items.length === 0 && (
            <p data-testid="agent-history-empty" className="text-sm text-muted-foreground">
              No runs yet.
            </p>
          )}
          {runs.data && runs.data.items.length > 0 && (
            <ul className="divide-y" data-testid="agent-history-list">
              {runs.data.items.map((run) => {
                const duration = runDurationMs({
                  started_at: run.started_at,
                  completed_at: run.completed_at,
                });
                return (
                  <li
                    key={run.id}
                    className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm"
                    data-testid={`agent-history-row-${run.id}`}
                  >
                    <div className="flex flex-col">
                      <span className="font-mono text-xs">{run.id}</span>
                      <span className="text-xs text-muted-foreground">
                        v{run.agent_version} · {formatTimestamp(run.created_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span>{formatCostMicros(run.usage.cost_micros)}</span>
                      <span>{formatDurationMs(duration)}</span>
                      <Badge variant={runStatusToBadgeVariant(run.status)}>{run.status}</Badge>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default AgentHistoryPage;
